import { yupResolver } from '@hookform/resolvers/yup'
import { DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import { dateOnly, InferType, number, object, ref } from '@if/core/yup'
import { EPaymentMethod } from '@src/types/constants'
import { useUser } from '@src/providers/UserProvider'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { useMemo } from 'react'
import { Resolver } from 'react-hook-form'

export const getChangeRegularPmtSchema = (canBypassAcpLock: boolean) =>
  object({
    amount: number()
      .required()
      .moreThan(0)
      .test(
        'test-sameAmount',
        () => t('loan.payment.changeRegularPmtDialog.errors.sameAmount'),
        (value, context) => {
          const fromDate = context.resolve<DateOnly | null>(ref('fromDate'))
          if (!fromDate) return true
          const simulation = context.resolve<CalendarSimulation>(ref('$simulation'))
          return value !== simulation.getRepaymentConfigAtDate(fromDate).amount
        },
      ),
    fromDate: dateOnly()
      .required()
      .test(
        'test-validMinimumFromDate',
        () => t('loan.payment.changeRegularPmtDialog.errors.invalidFromDate'),
        (value, context) => {
          if (!value) return true
          const simulation = context.resolve<CalendarSimulation>(ref('$simulation'))
          const minimumDate = simulation.getNextValidActionDate(EPaymentMethod.PreAuthorizedPayment, canBypassAcpLock)
          return value >= minimumDate
        },
      )
      .test(
        'test-validPaymentDate',
        () => t('loan.payment.changeRegularPmtDialog.errors.invalidPaymentDate'),
        (value, context) => {
          if (!value) return true
          const simulation = context.resolve<CalendarSimulation>(ref('$simulation'))
          const operations = simulation.operationInformations
          if (!operations) return true
          return operations.some(
            (oi) => oi.isCustomerPaymentOperation() && oi.operation.effectiveDate.getTime() === value.getTime(),
          )
        },
      ),
  })

export interface ChangeRegularPmtSchemaContext {
  simulation: CalendarSimulation
}

export type ChangeRegularPmtSchemaType = InferType<ReturnType<typeof getChangeRegularPmtSchema>>

export const useChangeRegularPmtResolver = () => {
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false
  return useMemo<Resolver<ChangeRegularPmtSchemaType, ChangeRegularPmtSchemaContext>>(
    () => yupResolver(getChangeRegularPmtSchema(canBypassAcpLock)),
    [canBypassAcpLock],
  )
}
